import { Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { filter, Subscription } from 'rxjs';
import { AlertType } from '../alert-card/alert-card.component';
import { CertificadoIfbService } from '../../services/apis/integracao-financas-banco/certificado-ifb.service';
import { CertificadosStatusResponse, CertificadoStatusItem } from '../../services/responses/integracao-financas-banco/certificados-status.response';
import { CertificadoEmpresaA1Service } from '../../services/apis/dfe/certificado_empresa_a1.service';
import { GetCertificadoEmpresaA1Response } from '../../services/responses/dfe/certificado_empresa_a1.response';
import { FinancasLookupService } from '../../services/apis/financas/financas-lookup.service';
import { StatusCertificadoEnum } from '../../enums/certificado/status-certificado.enum';
import { FluxoNegocioEnum } from '../../enums/integracao-financas-banco/fluxo-negocio.enum';
import { AuthService } from '../../services/apis/usuario/auth.service';
import { MaskService } from '../../services/mask.service';
import { ConfiguracaoDFeGeralService } from '../../services/apis/dfe/configuracao-dfe-geral.service';
import { ConfiguracaoDFeGeralStruct } from '../../services/structs/dfe/configuracao-dfe-geral.struct';
import { TipoAmbienteEnum } from '../../enums/dfe/tipo-ambiente.enum';

type TipoAlertaBanner = Extract<AlertType, 'neutral' | 'warning' | 'error'>;

/** Um alerta renderizado como uma barra empilhada no topo. */
interface AlertaBanner {
    id: string;                 // chave de identidade/dismiss
    prefixo: string;            // "Aviso." nos níveis fecháveis; vazio nos que não fecham
    mensagem: string;
    rota: string;               // destino ao clicar no link
    rotasExibicao: string[];    // telas em que o alerta aparece (prefixos de rota)
    textoLink: string;          // texto do link ao fim da mensagem
    podeFechar: boolean;
    tipoAlerta: TipoAlertaBanner;
    icone: string;
}

/**
 * Banner de alertas global, renderizado no app.component (pai de todos) e exibido no topo das telas
 * autenticadas relacionadas a cada alerta (rotasExibicao). Mostra UMA barra por alerta - nada é escondido
 * por sobreposição. Cinza/amarelo são fecháveis individualmente; vermelho não fecha. O clique ("Clique
 * aqui") leva à tela de regularização.
 *
 * Fontes de alertas, cada uma no seu dono: certificado fiscal da empresa (API DFe, métodos
 * *CertificadoFiscal), certificados de boleto/PIX (API IntegracaoFinancasBanco, métodos
 * *CertificadosBancarios) e ambiente de homologação da configuração geral de notas fiscais (métodos
 * *Ambiente). Cada fonte guarda a última resposta e a pilha junta todas (montarPilha). Fica fixo no topo
 * e empurra o conteúdo publicando a própria altura na CSS var global `--alert-banner-height` (styles.css).
 */
@Component({
    selector: 'app-alert-banner',
    standalone: true,
    imports: [CommonModule, MatIconModule],
    templateUrl: './alert-banner.component.html',
    styleUrls: ['./alert-banner.component.css'],
})
export class AlertBannerComponent implements OnInit, OnDestroy {
    public alertas: AlertaBanner[] = [];

    private subscriptions = new Subscription();
    private resizeObserver?: ResizeObserver;
    private readonly DISMISS_PREFIX = 'ats_alert_banner_dismissed_';
    // Rotas sem AuthGuard (ver app.routes.ts) - o banner nunca aparece nelas.
    private readonly ROTAS_PUBLICAS_PREFIXOS = ['/cobranca', '/auth', '/portal'];
    // Telas em que cada grupo de alertas aparece. '/home' é só a página inicial (ver exibeNaTelaAtual).
    private readonly ROTAS_ALERTA_FISCAL = ['/home', '/configuracoes/notas-fiscais', '/notas-fiscais', '/vendas'];
    private readonly ROTAS_ALERTA_BANCARIO = ['/home', '/configuracoes/financas', '/financas'];
    private readonly isBrowser: boolean;

    // Peso do tipo do alerta: ordena a pilha (mais grave primeiro) e é guardado no dismiss da sessão,
    // para o alerta fechado voltar se piorar.
    private readonly PESO_TIPO_ALERTA: Record<TipoAlertaBanner, number> = { error: 3, warning: 2, neutral: 1 };

    // Status do certificado que gera alerta. Valido não gera.
    private readonly ALERTA_POR_STATUS: Partial<Record<StatusCertificadoEnum, { tipoAlerta: TipoAlertaBanner; podeFechar: boolean; icone: string }>> = {
        [StatusCertificadoEnum.NaoConfigurado]: { tipoAlerta: 'error', podeFechar: false, icone: 'error' },
        [StatusCertificadoEnum.ProximoDoVencimento]: { tipoAlerta: 'neutral', podeFechar: true, icone: 'warning' },
        [StatusCertificadoEnum.VencimentoIminente]: { tipoAlerta: 'warning', podeFechar: true, icone: 'warning' },
        [StatusCertificadoEnum.Vencido]: { tipoAlerta: 'error', podeFechar: false, icone: 'error' },
    };

    // Empresa cujas fontes já foram carregadas; ao trocar de empresa, todas recarregam.
    private idEmpresaCarregada: number | null = null;

    // ---- Fonte "Certificados bancários" (boleto/PIX) ----
    private ultimaRespostaCertificadosBancarios: CertificadosStatusResponse | null = null;
    private nomesContasFinanceiras = new Map<number, string>();

    // ---- Fonte "Ambiente" (homologação na configuração geral de notas fiscais) ----
    private ultimaConfiguracaoDFeGeral: ConfiguracaoDFeGeralStruct | null = null;

    // ---- Fonte "Certificado fiscal" (e-CNPJ), avaliada só quando a empresa emite NF-e ou NFC-e ----
    private ultimaRespostaCertificadoFiscal: GetCertificadoEmpresaA1Response | null = null;

    constructor(
        private certificadoIfbService: CertificadoIfbService,
        private certificadoEmpresaA1Service: CertificadoEmpresaA1Service,
        private financasLookupService: FinancasLookupService,
        private configuracaoDFeGeralService: ConfiguracaoDFeGeralService,
        private authService: AuthService,
        private maskService: MaskService,
        private router: Router,
        @Inject(PLATFORM_ID) platformId: object,
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    // Observa a pilha (quando visível) para publicar a altura real na CSS var e empurrar o layout.
    @ViewChild('barra') set barraRef(ref: ElementRef<HTMLElement> | undefined) {
        if (!this.isBrowser) return;
        this.resizeObserver?.disconnect();
        const el = ref?.nativeElement;
        if (el) {
            this.aplicarAltura(el.offsetHeight);
            this.resizeObserver = new ResizeObserver(() => this.aplicarAltura(el.offsetHeight));
            this.resizeObserver.observe(el);
        } else {
            this.limparAltura();
        }
    }

    ngOnInit(): void {
        if (!this.isBrowser) return; // SSR: banner só existe no browser

        this.tentarCarregar();

        // Reavalia a cada navegação: carrega quando logar e reavalia visibilidade (respeitando o dismissal).
        this.subscriptions.add(
            this.router.events
                .pipe(filter(evt => evt instanceof NavigationEnd))
                .subscribe(() => this.tentarCarregar()),
        );

        // Quando um certificado de boleto/PIX é alterado, reconsulta ignorando o cache.
        this.subscriptions.add(
            this.certificadoIfbService.alteracoes$.subscribe(() => {
                if (this.authService.isAuthenticated()) this.carregarCertificadosBancarios(true);
            }),
        );

        // Quando o certificado da empresa (e-CNPJ) é enviado ou removido, reconsulta o certificado fiscal.
        this.subscriptions.add(
            this.certificadoEmpresaA1Service.alteracoes$.subscribe(() => {
                if (this.authService.isAuthenticated()) this.carregarCertificadoFiscal();
            }),
        );

        // Quando a configuração geral de notas fiscais é salva, reconsulta o ambiente (e o certificado fiscal).
        this.subscriptions.add(
            this.configuracaoDFeGeralService.alteracoes$.subscribe(() => {
                if (this.authService.isAuthenticated()) this.carregarAmbiente();
            }),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.resizeObserver?.disconnect();
        this.limparAltura();
    }

    /** Só consulta as fontes quando autenticado; recarrega todas ao trocar de empresa. */
    private tentarCarregar(): void {
        // Em rota pública (login, cobrança, portal do fornecedor) o banner nunca aparece, mesmo com
        // token válido no localStorage (ex.: navegador fechado sem logout). Apenas esconde - sem limpar
        // cache/dismiss: ao navegar para uma rota autenticada o próprio NavigationEnd reexibe o banner.
        if (this.emRotaPublica()) {
            this.alertas = [];
            this.limparAltura();
            return;
        }

        // Sem login (ex.: tela de login/logout) o banner não pode aparecer - esconde e limpa o estado.
        if (!this.authService.isAuthenticated()) {
            this.resetar();
            return;
        }

        const idEmpresa = this.authService.getUserInfos()?.idEmpresa ?? 0;
        if (idEmpresa <= 0) {
            this.resetar();
            return;
        }

        if (idEmpresa === this.idEmpresaCarregada) {
            this.montarPilha();
            return;
        }

        this.idEmpresaCarregada = idEmpresa;
        this.carregarCertificadosBancarios();
        this.carregarAmbiente();
    }

    /** Rota atual é pública? Raiz ('/') é o login - tolera query string (ex.: '/?token=...' do super-acesso). */
    private emRotaPublica(): boolean {
        const url = this.router.url;
        if (url === '/' || url.startsWith('/?')) return true;
        return this.ROTAS_PUBLICAS_PREFIXOS.some(prefixo => url.startsWith(prefixo));
    }

    /**
     * Esconde o banner, limpa o deslocamento do layout e apaga qualquer cache/dismiss persistido.
     * Usado quando não há login (tela de login/logout) - o banner não pode aparecer em nenhuma hipótese.
     */
    private resetar(): void {
        this.alertas = [];
        this.ultimaRespostaCertificadosBancarios = null;
        this.nomesContasFinanceiras = new Map<number, string>();
        this.idEmpresaCarregada = null;
        this.ultimaConfiguracaoDFeGeral = null;
        this.ultimaRespostaCertificadoFiscal = null;
        this.limparAltura();
        this.certificadoIfbService.limparTodoCache();
        this.limparDismissTudo();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Fonte de alertas: Certificados bancários (vencimento de boleto / PIX)
    // ─────────────────────────────────────────────────────────────────────────

    private carregarCertificadosBancarios(forceRefresh: boolean = false): void {
        this.subscriptions.add(
            this.certificadoIfbService.getStatus(forceRefresh).subscribe({
                next: response => {
                    if (response.isError) return;
                    this.ultimaRespostaCertificadosBancarios = response;
                    this.carregarNomesContasFinanceiras(response.certificados);
                },
                error: () => { /* silencioso - banner apenas não aparece */ },
            }),
        );
    }

    /**
     * O status dos certificados não traz o nome da conta: vem do lookup de contas financeiras da API
     * Financas, consultado só quando algum certificado gera alerta. Sem o nome, o texto usa "de um banco".
     */
    private carregarNomesContasFinanceiras(certificados: CertificadoStatusItem[]): void {
        if (!certificados.some(c => c.statusCertificado > StatusCertificadoEnum.Valido)) {
            this.montarPilha();
            return;
        }

        this.subscriptions.add(
            this.financasLookupService.listContaFinanceiraSelect().subscribe({
                next: response => {
                    if (!response.isError) {
                        this.nomesContasFinanceiras = new Map(response.list.map(c => [c.id, c.descricao] as [number, string]));
                    }
                    this.montarPilha();
                },
                error: () => this.montarPilha(),
            }),
        );
    }

    private montarAlertaCertificadoBancario(certificado: CertificadoStatusItem): AlertaBanner | null {
        // NaoConfigurado não se aplica a banco (a API só lista configs com certificado).
        if (certificado.statusCertificado <= StatusCertificadoEnum.Valido) return null;
        const config = this.ALERTA_POR_STATUS[certificado.statusCertificado];
        if (!config) return null;

        const dataFmt = this.formatarData(certificado.dataVencimento);
        const vencido = certificado.statusCertificado === StatusCertificadoEnum.Vencido;
        const nomeConta = this.nomesContasFinanceiras.get(certificado.idContaFinanceira)?.trim();
        const nome = nomeConta ? `"${nomeConta}"` : 'de um banco';
        // Uma conta pode ter certificados separados de Boleto e de PIX - o tipo entra no texto.
        const tipoLabel = certificado.tipo === FluxoNegocioEnum.Pix ? 'Pix' : 'Boleto';
        return {
            id: `cert-banco-${certificado.idContaFinanceira}-${certificado.tipo}`,
            prefixo: config.podeFechar ? 'Aviso.' : '',
            mensagem: vencido
                ? `Não é possível emitir cobranças até corrigir o certificado de ${tipoLabel} do banco ${nome}, que expirou em ${dataFmt}.`
                : `O certificado de ${tipoLabel} do banco ${nome} vence em ${dataFmt}. Renove antes do vencimento para não interromper a emissão.`,
            rota: '/configuracoes/financas/contas-financeiras',
            rotasExibicao: this.ROTAS_ALERTA_BANCARIO,
            textoLink: 'Clique aqui para corrigir.',
            podeFechar: config.podeFechar,
            tipoAlerta: config.tipoAlerta,
            icone: config.icone,
        };
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Fonte de alertas: Ambiente de homologação (configuração geral de notas fiscais)
    // ─────────────────────────────────────────────────────────────────────────

    /** Carrega a configuração geral e, em seguida, o certificado fiscal (que depende de a empresa emitir). */
    private carregarAmbiente(): void {
        this.subscriptions.add(
            this.configuracaoDFeGeralService.get().subscribe({
                next: response => {
                    if (response.isError) return;
                    this.ultimaConfiguracaoDFeGeral = response.configuracaoDFeGeral;
                    this.carregarCertificadoFiscal();
                },
                error: () => { /* silencioso - banner apenas não aparece */ },
            }),
        );
    }

    private empresaEmiteNotaFiscal(): boolean {
        const configuracao = this.ultimaConfiguracaoDFeGeral;
        return !!configuracao && (configuracao.emiteNFe || configuracao.emiteNFCe);
    }

    /**
     * Aviso de homologação: só para a empresa que emite NF-e ou NFC-e, porque o ambiente da configuração
     * geral só vale para quem emite. Leva à tela Gerais, onde o ambiente é trocado.
     */
    private montarAlertaAmbiente(): AlertaBanner | null {
        if (!this.empresaEmiteNotaFiscal()
            || this.ultimaConfiguracaoDFeGeral?.idTipoAmbiente !== TipoAmbienteEnum.Homologacao) return null;

        return {
            id: 'ambiente-homologacao',
            prefixo: 'Ambiente de Homologação.',
            mensagem: 'As notas fiscais emitidas não têm validade jurídica.',
            rota: '/configuracoes/notas-fiscais/gerais',
            rotasExibicao: this.ROTAS_ALERTA_FISCAL,
            textoLink: 'Clique aqui para alterar o ambiente.',
            podeFechar: true,
            tipoAlerta: 'warning',
            icone: 'science',
        };
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Fonte de alertas: Certificado fiscal da empresa (e-CNPJ)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Só consulta quando a empresa emite NF-e ou NFC-e. Erro da API (ex.: usuário sem permissão de
     * notas fiscais) não gera alerta.
     */
    private carregarCertificadoFiscal(): void {
        if (!this.empresaEmiteNotaFiscal()) {
            this.ultimaRespostaCertificadoFiscal = null;
            this.montarPilha();
            return;
        }

        this.subscriptions.add(
            this.certificadoEmpresaA1Service.getCertificadoAtual().subscribe({
                next: response => {
                    this.ultimaRespostaCertificadoFiscal = response.isError ? null : response;
                    this.montarPilha();
                },
                error: () => {
                    this.ultimaRespostaCertificadoFiscal = null;
                    this.montarPilha();
                },
            }),
        );
    }

    private montarAlertaCertificadoFiscal(): AlertaBanner | null {
        const response = this.ultimaRespostaCertificadoFiscal;
        if (!response || response.statusCertificado == null || !this.empresaEmiteNotaFiscal()) return null;
        const config = this.ALERTA_POR_STATUS[response.statusCertificado];
        if (!config) return null;

        const alerta: AlertaBanner = {
            id: 'cert-empresa',
            prefixo: config.podeFechar ? 'Aviso.' : '',
            mensagem: '',
            rota: '/configuracoes/notas-fiscais/certificado',
            rotasExibicao: this.ROTAS_ALERTA_FISCAL,
            textoLink: 'Clique aqui para corrigir.',
            podeFechar: config.podeFechar,
            tipoAlerta: config.tipoAlerta,
            icone: config.icone,
        };

        const dataFmt = this.formatarData(response.certificadoEmpresaA1?.dataVencimento ?? null);
        switch (response.statusCertificado) {
            case StatusCertificadoEnum.NaoConfigurado:
                alerta.mensagem = 'Não é possível emitir notas fiscais: a empresa não tem certificado digital (e-CNPJ) configurado.';
                alerta.textoLink = 'Clique aqui para configurar.';
                break;
            case StatusCertificadoEnum.Vencido:
                alerta.mensagem = `Não é possível emitir notas fiscais até corrigir o certificado da empresa (e-CNPJ), que expirou em ${dataFmt}.`;
                break;
            default:
                alerta.mensagem = `O certificado da empresa (e-CNPJ) vence em ${dataFmt}. Renove antes do vencimento para não interromper a emissão.`;
                break;
        }
        return alerta;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Genérico: pilha, exibição, dismiss, altura e formatação
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Junta as fontes na pilha, só com os alertas da tela atual e respeitando os dismissals, mais graves
     * primeiro (vermelho, amarelo, cinza). Alerta fora da tela não conta como fechado.
     */
    private montarPilha(): void {
        const lista: (AlertaBanner | null)[] = [this.montarAlertaCertificadoFiscal()];
        for (const certificado of this.ultimaRespostaCertificadosBancarios?.certificados ?? []) {
            lista.push(this.montarAlertaCertificadoBancario(certificado));
        }
        lista.push(this.montarAlertaAmbiente());

        const dismissed = this.getDismissedMap();
        this.alertas = lista
            .filter((alerta): alerta is AlertaBanner =>
                !!alerta && this.exibeNaTelaAtual(alerta) && this.deveMostrar(alerta, dismissed))
            .sort((a, b) => this.PESO_TIPO_ALERTA[b.tipoAlerta] - this.PESO_TIPO_ALERTA[a.tipoAlerta]);
    }

    /** Rota atual (sem query string) é uma das telas do alerta? '/home' vale só para a página inicial. */
    private exibeNaTelaAtual(alerta: AlertaBanner): boolean {
        const url = this.router.url.split('?')[0];
        return alerta.rotasExibicao.some(rota =>
            rota === '/home' ? url === rota : url === rota || url.startsWith(rota + '/'));
    }

    /** Alerta que não fecha sempre aparece; o fechado volta se o tipo piorar desde o dismiss. */
    private deveMostrar(alerta: AlertaBanner, dismissed: Record<string, number>): boolean {
        if (!alerta.podeFechar) return true;
        return this.PESO_TIPO_ALERTA[alerta.tipoAlerta] > (dismissed[alerta.id] ?? 0);
    }

    public onClick(alerta: AlertaBanner): void {
        this.router.navigate([alerta.rota]);
    }

    public fechar(alerta: AlertaBanner, event: Event): void {
        event.stopPropagation();
        if (!alerta.podeFechar) return;
        const dismissed = this.getDismissedMap();
        dismissed[alerta.id] = this.PESO_TIPO_ALERTA[alerta.tipoAlerta];
        this.setDismissedMap(dismissed);
        this.alertas = this.alertas.filter(a => a.id !== alerta.id);
    }

    // Reusa o formatador de data do MaskService (dd/MM/yyyy) e anexa hora:minuto.
    private formatarData(data: Date | string | null): string {
        if (!data) return '';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '';
        const dataFmt = this.maskService.formatDateToString(d);
        const hh = d.getHours().toString().padStart(2, '0');
        const mm = d.getMinutes().toString().padStart(2, '0');
        return `${dataFmt} ${hh}:${mm}`;
    }

    private aplicarAltura(altura: number): void {
        if (!this.isBrowser) return;
        if (altura > 0) {
            document.documentElement.style.setProperty('--alert-banner-height', `${altura}px`);
            document.body.classList.add('alert-banner-active');
        } else {
            this.limparAltura();
        }
    }

    private limparAltura(): void {
        if (!this.isBrowser) return;
        document.body.classList.remove('alert-banner-active');
        document.documentElement.style.removeProperty('--alert-banner-height');
    }

    private get dismissKey(): string {
        return this.DISMISS_PREFIX + (this.authService.getUserInfos()?.idEmpresa ?? 0);
    }

    private getDismissedMap(): Record<string, number> {
        if (!this.isBrowser) return {};
        try {
            const raw = sessionStorage.getItem(this.dismissKey);
            return raw ? JSON.parse(raw) as Record<string, number> : {};
        } catch {
            return {};
        }
    }

    private setDismissedMap(map: Record<string, number>): void {
        if (!this.isBrowser) return;
        try {
            sessionStorage.setItem(this.dismissKey, JSON.stringify(map));
        } catch {
            /* sessionStorage indisponível - ignora */
        }
    }

    private limparDismissTudo(): void {
        if (!this.isBrowser) return;
        try {
            const chaves: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const chave = sessionStorage.key(i);
                if (chave && chave.startsWith(this.DISMISS_PREFIX)) chaves.push(chave);
            }
            chaves.forEach(chave => sessionStorage.removeItem(chave));
        } catch {
            /* sessionStorage indisponível - ignora */
        }
    }
}
